/**
 * Content normalization utilities.
 *
 * The OpenClaw gateway may return message content in two formats:
 * - Plain string: `"Hello"`
 * - Array of content blocks: `[{ type: "text", text: "Hello" }]`
 *
 * The content block format comes from Anthropic's API. This module
 * provides a normalizer that accepts either format and always returns
 * a plain string for UI rendering.
 */

/** A single content block from the Anthropic API */
interface ContentBlock {
  type: string;
  text?: string;
}

/**
 * Normalize message content to a plain string.
 *
 * Handles both wire formats the gateway may return:
 * - If already a string, returns it as-is.
 * - If an array of content blocks, extracts and joins all `text` fields.
 * - Falls back to empty string for null/undefined.
 *
 * @param content - Raw content from the gateway (string or content block array)
 * @returns Plain string suitable for UI rendering
 *
 * @example
 * ```ts
 * normalizeContent("Hello")
 * // → "Hello"
 *
 * normalizeContent([{ type: "text", text: "Hello " }, { type: "text", text: "world" }])
 * // → "Hello world"
 *
 * normalizeContent([{ type: "image", source: { ... } }])
 * // → "" (non-text blocks are skipped)
 * ```
 */
export function normalizeContent(content: unknown): string {
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .filter(
        (block: unknown): block is ContentBlock =>
          block != null &&
          typeof block === 'object' &&
          typeof (block as ContentBlock).text === 'string',
      )
      .map((block) => block.text!)
      .join('');
  }

  // Fallback for null, undefined, or unexpected types
  return String(content ?? '');
}
